import { useEffect, useState } from "react";

import { Modal } from "@smart/itops-sb-design-system-dom";
import { isNotNullOrUndefined } from "@smart/itops-utils-basic";

import { getGroupUpdateOptions, mapMatterField } from "./helpers";
import { useContactDetailsMappedFields, useMappedMatterFields } from "./hooks";
import { Picker } from "./picker";
import { PickerSelection } from "./types";
import {
  GqlField,
  GqlGroup,
  GqlMatterLayout,
  GqlMatterType,
} from "../../../types";
import { fieldComponentIcons } from "../../constants";
import { CreateMappedFields, LoadMatterFields } from "../../types";
import { isContactDetailsGroup as checkIsContactDetailsGroup } from "../../utils";

export type MappedFieldsOptions = {
  beforePosition?: string;
  afterPosition?: string;
  destinationGroupUri?: string;
  fixedSelection?: PickerSelection;
};

export type MappedFieldsModalProps = {
  onClose: () => void;
  createFields: CreateMappedFields;
  matterLayouts?: GqlMatterLayout[];
  matterTypes?: GqlMatterType[];
  loadMatterFields: LoadMatterFields;
  groups: GqlGroup[];
  fields: GqlField[];
  mappedFieldsOptions: MappedFieldsOptions | undefined;
  loading?: boolean;
};

export const MappedFieldsModal = ({
  mappedFieldsOptions,
  onClose,
  matterLayouts,
  matterTypes,
  loadMatterFields,
  createFields,
  fields,
  groups,
  loading: dataLoading,
}: MappedFieldsModalProps) => {
  const [isSaving, setIsSaving] = useState(false);

  const destinationGroup = groups.find(
    (g) => g.uri === mappedFieldsOptions?.destinationGroupUri,
  );
  const isContactDetailsGroup = checkIsContactDetailsGroup(
    destinationGroup?.values,
  );

  const initial: PickerSelection = {
    layout: undefined,
    prefix: [],
    fields: [],
    components: [],
  };

  const [selection, setSelection] = useState<PickerSelection>(initial);

  useEffect(() => {
    if (!mappedFieldsOptions) {
      setSelection(initial);
      setIsSaving(false);
    } else if (mappedFieldsOptions.fixedSelection) {
      setSelection(mappedFieldsOptions.fixedSelection);
    }
  }, [mappedFieldsOptions]);

  const {
    isLoading: isLoadingMatterFields,
    currentLayoutComponent,
    matterFields,
    existingMappedFields,
  } = useMappedMatterFields({
    selection,
    fields,
    matterTypes,
    groups,
    destinationGroup,
    loadMatterFields,
  });

  const {
    groupMappedFieldNames,
    fieldsToAdd,
    fieldUrisToRemove,
    hasSelectionChanged,
  } = useContactDetailsMappedFields({
    destinationGroup,
    fields,
    matterFields,
    isLoadingMatterFields,
    selection,
    setSelection,
  });

  const onSaveChanges = async () => {
    setIsSaving(true);

    try {
      if (
        selection.layout &&
        (fieldsToAdd.length || fieldUrisToRemove.length)
      ) {
        await createFields({
          beforePosition: mappedFieldsOptions?.beforePosition,
          afterPosition: mappedFieldsOptions?.afterPosition,
          destinationGroupUri: mappedFieldsOptions?.destinationGroupUri,
          newFields: fieldsToAdd
            .map(mapMatterField(selection.layout))
            .filter(isNotNullOrUndefined),
          ...(fieldUrisToRemove.length
            ? { removedFieldUris: fieldUrisToRemove }
            : {}),
          // Avoid converting a contactDetails group into layoutContact via getGroupUpdateOptions
          group: isContactDetailsGroup
            ? undefined
            : getGroupUpdateOptions({
                selection,
                currentLayoutComponent,
                groupUri: mappedFieldsOptions?.destinationGroupUri,
              }),
        });
      }
      onClose();
    } catch (e) {
      console.error(e);
      setIsSaving(false);
    }
  };

  return (
    <Modal
      className="[&_.modal-container]:min-h-[min(90rem,95vh)]"
      open={!!mappedFieldsOptions}
      onClose={onClose}
      loading={isSaving}
      header={{
        icon: fieldComponentIcons.mapped.name,
        iconBgColor: fieldComponentIcons.mapped.bgColor,
        text: "Mapped fields",
      }}
      footer={{
        buttons: [
          {
            text: "Cancel",
            variant: "secondarySubtle",
            onClick: onClose,
          },
          {
            key: "addMappedFields",
            text: "Confirm",
            variant: "primaryDefault",
            disabled:
              !selection.layout ||
              isLoadingMatterFields ||
              isSaving ||
              dataLoading ||
              !hasSelectionChanged,
            onClick: onSaveChanges,
          },
        ],
      }}
    >
      <Picker
        loading={dataLoading || isLoadingMatterFields || false}
        isLoadingMatterFields={isLoadingMatterFields}
        fixedSelection={mappedFieldsOptions?.fixedSelection}
        selection={selection}
        setSelection={setSelection}
        matterTypes={matterTypes || []}
        layouts={matterLayouts || []}
        fields={matterFields || []}
        existingMappedFields={existingMappedFields}
        groupMappedFieldNames={groupMappedFieldNames}
        group={destinationGroup}
        groups={groups}
      />
    </Modal>
  );
};
